/**
 * Reporting & Filtering Types
 *
 * Shared types for the Builder's Bookings and Quotes reporting surfaces:
 * server-side filtering, sorting, CSV export column selection, and saved views.
 *
 * These filters are translated into Supabase queries in exactly ONE place per
 * resource (`apps/builder/src/lib/reporting/buildBookingsQuery.ts` /
 * `buildQuotesQuery.ts`), which the table view, the count queries and the CSV
 * export all share. That is what guarantees an export matches what the operator
 * filtered — see the scope docs at `.claude/scope/bookings-quotes-reporting/`.
 *
 * All shapes are readonly: filters flow through React Query keys, so accidental
 * mutation would silently desync the cache from the UI.
 */

// =============================================================================
// Primitives
// =============================================================================

/**
 * A HALF-OPEN date range `[from, to)`. Values are ISO-8601 strings in UTC.
 *
 * Half-open, not inclusive: an inclusive upper bound on a timestamp column
 * forces a `23:59:59.999` fencepost that drops rows in the final millisecond
 * and has to be recomputed around DST. `>= from AND < to` is exact. Query
 * builders therefore apply `.gte(from)` and `.lt(to)`.
 *
 * Presets ("This month") are resolved in the ACCOUNT's timezone before being
 * serialized to UTC — see `reporting/datePresets.ts`. Storing UTC keeps saved
 * views and shareable URLs unambiguous.
 */
export interface DateRangeFilter {
  readonly from?: string
  readonly to?: string
}

export interface TableSort<F extends string = string> {
  readonly field: F
  readonly direction: 'asc' | 'desc'
}

export type ReportResource = 'bookings' | 'quotes'

// =============================================================================
// Booking filters
// =============================================================================

export type BookingSortField =
  | 'created_at'
  | 'pickup_datetime'
  | 'total_amount'
  | 'balance_due'
  | 'customer_name'
  | 'booking_status'
  | 'payment_status'

export interface BookingFilters {
  /** Free-text over customer name/email, confirmation code, pickup address. */
  readonly search?: string

  /** When the trip happens. */
  readonly pickupDate?: DateRangeFilter
  /** When the booking was taken. Deliberately separate from pickupDate — */
  /** conflating "trips in March" with "bookings taken in March" is a common */
  /** reporting error. */
  readonly createdDate?: DateRangeFilter

  readonly bookingStatus?: readonly BookingStatusValue[]
  readonly paymentStatus?: readonly PaymentStatusValue[]

  readonly amountMin?: number
  readonly amountMax?: number
  /** Outstanding balance > 0. Backed by the `balance_due` generated column. */
  readonly hasBalanceDue?: boolean

  readonly serviceType?: readonly BookingServiceTypeValue[]
  readonly vehicleIds?: readonly string[]
  readonly passengerMin?: number
  readonly passengerMax?: number
  readonly roundTrip?: boolean
  readonly airportPickup?: boolean
  readonly airportDropoff?: boolean

  /**
   * Include `is_test` bookings. Defaults to FALSE when absent — a revenue
   * report that silently includes onboarding smoke-test bookings is worse than
   * no report at all.
   */
  readonly includeTest?: boolean

  /**
   * Marketing attribution. `bookings` has NO utm_* columns; these are matched
   * by joining through `booking_quotes`. That join is an INNER join, so it
   * excludes bookings with no quote row (phone-ins, legacy). The query builder
   * therefore only applies it when one of these is actually set.
   */
  readonly utmSource?: readonly string[]
  readonly utmMedium?: readonly string[]
  readonly utmCampaign?: readonly string[]
}

// =============================================================================
// Quote filters
// =============================================================================

export type QuoteSortField =
  | 'created_at'
  | 'pickup_datetime'
  | 'total_amount'
  | 'expires_at'
  | 'status'

export interface QuoteFilters {
  readonly search?: string

  readonly pickupDate?: DateRangeFilter
  readonly createdDate?: DateRangeFilter

  readonly status?: readonly QuoteStatusValue[]

  readonly amountMin?: number
  readonly amountMax?: number

  /** `booking_quotes` allows only point_to_point | hourly (not airport/event). */
  readonly serviceType?: readonly QuoteServiceTypeValue[]
  readonly vehicleIds?: readonly string[]
  readonly passengerMin?: number
  readonly passengerMax?: number
  readonly roundTrip?: boolean
  readonly airportPickup?: boolean
  readonly airportDropoff?: boolean

  /** Quote-specific flags. */
  readonly isOperatorPriced?: boolean
  readonly isFlatRate?: boolean
  readonly hasDiscount?: boolean

  /** utm_* columns live directly on booking_quotes — no join required. */
  readonly utmSource?: readonly string[]
  readonly utmMedium?: readonly string[]
  readonly utmCampaign?: readonly string[]
}

// =============================================================================
// Enum options — MUST mirror the DB CHECK constraints
// =============================================================================
//
// Each list below is transcribed from a migration, not inferred. Keeping them
// in sync matters: an option the DB rejects yields an empty result set with no
// error, and a MISSING option makes real rows unreachable (which is the state
// the Bookings page shipped in — `no_show` was absent from its dropdown).

export interface FilterOption<T extends string = string> {
  readonly value: T
  readonly label: string
}

/** Source: supabase/migrations/20241205100000_create_bookings.sql:52 */
export const BOOKING_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  // Allowed by the DB but missing from the pre-reporting UI dropdown, which
  // made no-show bookings unfilterable.
  { value: 'no_show', label: 'No Show' },
] as const satisfies readonly FilterOption[]

export type BookingStatusValue = (typeof BOOKING_STATUS_OPTIONS)[number]['value']

/** Source: supabase/migrations/20241205100000_create_bookings.sql:48 */
export const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'failed', label: 'Failed' },
] as const satisfies readonly FilterOption[]

export type PaymentStatusValue = (typeof PAYMENT_STATUS_OPTIONS)[number]['value']

/**
 * Source: supabase/migrations/20241205100000_create_bookings.sql:11
 * Note this is a SUPERSET of the quote list — bookings additionally allow
 * 'airport' and 'event'.
 */
export const BOOKING_SERVICE_TYPE_OPTIONS = [
  { value: 'point_to_point', label: 'Point to Point' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'airport', label: 'Airport' },
  { value: 'event', label: 'Event' },
] as const satisfies readonly FilterOption[]

export type BookingServiceTypeValue =
  (typeof BOOKING_SERVICE_TYPE_OPTIONS)[number]['value']

/**
 * Source: supabase/migrations/20260127000000_create_booking_quotes.sql:31
 * Only two values — do NOT reuse BOOKING_SERVICE_TYPE_OPTIONS here or the
 * Quotes page renders options the DB can never return.
 */
export const QUOTE_SERVICE_TYPE_OPTIONS = [
  { value: 'point_to_point', label: 'Point to Point' },
  { value: 'hourly', label: 'Hourly' },
] as const satisfies readonly FilterOption[]

export type QuoteServiceTypeValue =
  (typeof QUOTE_SERVICE_TYPE_OPTIONS)[number]['value']

/** Source: supabase/migrations/20260611000000_add_operator_priced_quotes.sql:32-42 */
export const QUOTE_STATUS_OPTIONS = [
  { value: 'trip_details', label: 'Trip Details' },
  { value: 'vehicle_selected', label: 'Vehicle Selected' },
  { value: 'contact_provided', label: 'Contact Provided' },
  { value: 'quote_requested', label: 'Quote Requested' },
  { value: 'priced', label: 'Priced' },
  { value: 'converted', label: 'Converted' },
  { value: 'expired', label: 'Expired' },
] as const satisfies readonly FilterOption[]

export type QuoteStatusValue = (typeof QUOTE_STATUS_OPTIONS)[number]['value']

// =============================================================================
// Saved views
// =============================================================================

/**
 * A named filter preset, ALWAYS shared with everyone on the account.
 *
 * `filters` is persisted as JSONB rather than columns because the filter
 * surface is wide and expected to grow; it is validated on read (unknown keys
 * dropped) so an old view never throws after the filter shape changes.
 */
export interface SavedView {
  readonly id: string
  readonly accountId: string
  readonly resource: ReportResource
  readonly name: string
  readonly filters: Readonly<Record<string, unknown>>
  readonly sort: TableSort | null
  /** Column keys selected for CSV export, in registry order. */
  readonly columns: readonly string[]
  readonly createdBy: string | null
  readonly createdAt: string
  readonly updatedAt: string
}

export interface SaveViewRequest {
  readonly resource: ReportResource
  readonly name: string
  readonly filters: Readonly<Record<string, unknown>>
  readonly sort: TableSort | null
  readonly columns: readonly string[]
}

// =============================================================================
// Paged result envelope
// =============================================================================

export interface PagedResult<T> {
  readonly rows: readonly T[]
  /** Exact total from PostgREST `count`, NOT `rows.length`. */
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly totalPages: number
}
